import clsx from "clsx";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { ClientTypeFilterValue } from "../../ClientTypeFilter";

import { ALL_TIME_START } from "../../../SubIBReport/providers/getDefaultDateRange";
import { ALL_CLIENT_TYPES } from "../../ClientTypeFilter";
import { MobileFiltersBottomSheet } from "../../Common/controls/MobileFiltersBottomSheet";
import { useClientTradingReportContext } from "../providers/TradingReportProvider";

type MobileFiltersV2Props = {
  handleSearchInput: (value: string) => void;
  canViewPII: boolean;
  redactedFields?: string[];
  /** Gated by the part-3795-materialized-view-hoid flag. */
  showRebateProgramFilter?: boolean;
  /** Gated by the part-4039-master-sub-ib-phase-2 flag -- adds the "All
   * time" preset to the date options list. */
  showToolbarDateRange?: boolean;
  showClientTypeFilter?: boolean;
  hasToolbarLeading?: boolean;
  subIBsLoadFailed?: boolean;
  onRetrySubIBs?: () => void;
};

// Stable reference for the "nothing selected yet" fallback below. A fresh
// `[]` literal evaluated on every render would defeat MobileFiltersBottomSheet's
// reference-based effect and loop it forever once anything else re-renders.
const EMPTY_SELECTION: string[] = [];

export const MobileFiltersV2 = ({
  handleSearchInput,
  canViewPII,
  redactedFields,
  showRebateProgramFilter,
  showToolbarDateRange,
  showClientTypeFilter = false,
  hasToolbarLeading = false,
  subIBsLoadFailed,
  onRetrySubIBs,
}: MobileFiltersV2Props) => {
  const { t } = useTranslation();
  const {
    state: {
      searchQuery,
      selected: {
        rebateAccountIDs: selectedRebateAccountIDs,
        rebateProgramIDs: selectedRebateProgramIDs,
        clientTypes: selectedClientTypeValues,
        subIBIDs: selectedSubIBIDs,
      },
    },
    options: { rebateAccountIDs, rebateProgramIDs, subIBIDs },
    actions,
  } = useClientTradingReportContext();

  const [selectedDateOption, setSelectedDateOption] = useState("last30days");

  const selectedClientTypes =
    (selectedClientTypeValues as ClientTypeFilterValue[] | null) ??
    ALL_CLIENT_TYPES;

  // Date range always has a value in effect (a default date window), so it
  // always counts as an active filter -- matches the desktop Filter modal's
  // filterCount.
  const filterCount =
    1 +
    ((selectedRebateAccountIDs as string[] | null)?.length ? 1 : 0) +
    (showRebateProgramFilter &&
    (selectedRebateProgramIDs as string[] | null)?.length
      ? 1
      : 0) +
    (showClientTypeFilter && selectedClientTypes.length === 1 ? 1 : 0) +
    (showClientTypeFilter && (selectedSubIBIDs as string[] | null)?.length
      ? 1
      : 0);

  const handleApply = (params: {
    dateOption: string;
    dateRange: { from: Date; to: Date };
    rebateAccountIDs: string[];
    rebateProgramIDs: string[];
    clientTypes: ClientTypeFilterValue[];
    subIBIDs: string[];
  }) => {
    setSelectedDateOption(params.dateOption);
    actions.setDateRange(params.dateRange);
    actions.setSelected("rebateAccountIDs", params.rebateAccountIDs);
    actions.setSelected("rebateProgramIDs", params.rebateProgramIDs);
    if (showClientTypeFilter) {
      actions.setSelected("clientTypes", params.clientTypes);
      actions.setSelected("subIBIDs", params.subIBIDs);
    }
  };

  // Reset is draft-only -- MobileFiltersBottomSheet already resets its own
  // local draft (dates, rebate account/program selections) when its Reset
  // button is clicked. Nothing here should touch the report context; only
  // Apply may call an API.

  return (
    <div
      className={clsx(
        "sticky top-[64px] z-10 bg-surface-primary pb-4 pt-4",
        !hasToolbarLeading && "mt-6",
      )}
    >
      <MobileFiltersBottomSheet
        title={t("clientsPage_tradingReport_filters_filterButton")}
        dateSectionLabel={t("clientsPage_tradingReport_filters_dataTimeRange")}
        emptyMeansAll
        handleSearchInput={handleSearchInput}
        canViewPII={canViewPII}
        redactedFields={redactedFields}
        searchQuery={searchQuery}
        rebateAccountOptions={rebateAccountIDs}
        selectedRebateAccountIDs={
          (selectedRebateAccountIDs as string[] | null) ?? EMPTY_SELECTION
        }
        showRebateProgramFilter={showRebateProgramFilter}
        rebateProgramOptions={rebateProgramIDs}
        selectedRebateProgramIDs={
          (selectedRebateProgramIDs as string[] | null) ?? EMPTY_SELECTION
        }
        selectedDateOption={selectedDateOption}
        containerClassName="flex flex-row items-center gap-2"
        searchInputClassName="!outline-0 hover:!outline-0 focus:!outline-2 focus:!outline-stroke-focused"
        filterCount={filterCount}
        includeAllTime={showToolbarDateRange}
        allTimeStart={ALL_TIME_START}
        allTimeLabel={t(
          "clientsPage_tradingReport_filter_date_option_allTime",
          "All time",
        )}
        onApply={handleApply}
        showClientTypeFilter={showClientTypeFilter}
        subIBsLoadFailed={subIBsLoadFailed}
        onRetrySubIBs={onRetrySubIBs}
        selectedClientTypes={selectedClientTypes}
        subIBOptions={subIBIDs}
        selectedSubIBIDs={
          (selectedSubIBIDs as string[] | null) ?? EMPTY_SELECTION
        }
      />
    </div>
  );
};
